// -- Lifecycle Hooks
if (!customElements.get('new-product-related')) {
  customElements.define('new-product-related', class extends HTMLElement {
    connectedCallback() {
      // -- State and Variables
      this.form = this.closest('form');
      this.section = this.closest('[data-section-type="product"]');
      this.mainPrice = Number(this.dataset.mainPrice);
      this.variantPrices = JSON.parse(this.querySelector('[data-new-related-prices]')?.textContent || '{}');
      this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency, currencyDisplay: 'code'
      });
      this.onTotalChange = () => this.updateTotal();
      // Theme quantity buttons update the input during click delegation.
      this.onFormClick = () => requestAnimationFrame(() => { if (this.isConnected) this.updateTotal(); });
      this.onChange = this.sync.bind(this);
      this.onClick = this.step.bind(this);
      this.onReset = this.reset.bind(this);
      this.onSubmit = (event) => {
        if (!this.getItems().length) return;
        event.preventDefault();
        const button = this.form.querySelector('[data-action="add-to-cart"]');
        if (button && !button.disabled) button.click();
      };
      // -- Listeners
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.section.addEventListener('variant:changed', this.onReset);
      window.addEventListener('pageshow', this.onReset);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onTotalChange);
      this.form.addEventListener('change', this.onTotalChange);
      this.form.addEventListener('click', this.onFormClick);
      this.reset();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.section.removeEventListener('variant:changed', this.onReset);
      window.removeEventListener('pageshow', this.onReset);
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('input', this.onTotalChange);
      this.form.removeEventListener('change', this.onTotalChange);
      this.form.removeEventListener('click', this.onFormClick);
      this.form.classList.remove('new-product-has-related');
    }

    // -- Functions
    reset(event) {
      if (event?.type === 'variant:changed') this.mainPrice = event.detail.variant?.price ?? null;
      else {
        const variantId = this.form.querySelector('[name="id"]')?.value;
        this.mainPrice = this.variantPrices[variantId] ?? Number(this.dataset.mainPrice);
      }
      this.querySelectorAll('[data-new-related-check]').forEach(check => { check.checked = false; });
      this.querySelectorAll('[data-new-related-quantity]').forEach(input => { input.value = input.min; });
      this.sync();
    }

    sync() {
      this.querySelectorAll('[data-new-related-item]').forEach(row => {
        const check = row.querySelector('[data-new-related-check]');
        const input = row.querySelector('[data-new-related-quantity]');
        input.disabled = !check.checked || check.disabled;
        const min = Number(input.min);
        const step = Number(input.step);
        const max = input.max ? Number(input.max) : Infinity;
        input.value = this.quantity(input);
        row.querySelector('[data-new-related-step="-1"]').disabled = input.disabled || Number(input.value) <= min;
        row.querySelector('[data-new-related-step="1"]').disabled = input.disabled || Number(input.value) + step > max;
      });
      this.form.classList.toggle('new-product-has-related', this.getItems().length > 0);
      const checkout = this.form.querySelector('[data-new-related-checkout]');
      const addButton = this.form.querySelector('.product-form__add-button[data-action="add-to-cart"]');
      if (checkout && addButton) checkout.disabled = addButton.disabled;
      this.updateTotal();
    }

    updateTotal() {
      const output = this.querySelector('[data-new-related-total]');
      if (!output) return;
      const price = this.mainPrice;
      if (price === null) { output.textContent = '—'; return; }
      const mainQuantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      let total = price * (Number.isFinite(mainQuantity) ? Math.max(1, Math.floor(mainQuantity)) : 1);
      this.querySelectorAll('[data-new-related-check]:checked:not(:disabled)').forEach(check => {
        total += Number(check.dataset.price) * this.quantity(check.closest('[data-new-related-item]').querySelector('[data-new-related-quantity]'));
      });
      output.textContent = this.moneyFormatter.format(total / 100);
    }

    step(event) {
      const button = event.target.closest('[data-new-related-step]');
      if (!button || button.disabled) return;
      const input = button.closest('[data-new-related-item]').querySelector('[data-new-related-quantity]');
      input.value = Number(input.value) + Number(button.dataset.newRelatedStep) * Number(input.step);
      this.sync();
    }

    // The theme's existing add-to-cart handler reads this component; it owns the request and cart UI.
    getItems() {
      return Array.from(this.querySelectorAll('[data-new-related-check]:checked:not(:disabled)')).map(check => ({
        id: check.value,
        quantity: this.quantity(check.closest('[data-new-related-item]').querySelector('[data-new-related-quantity]'))
      }));
    }

    quantity(input) {
      const min = Number(input.min);
      const step = Number(input.step);
      const max = input.max ? Number(input.max) : Infinity;
      const value = Number(input.value);
      return Math.min(min + Math.floor((max - min) / step) * step,
        min + Math.max(0, Math.round(((Number.isFinite(value) ? value : min) - min) / step)) * step);
    }
  });
}
